/**
 * Toast.tsx — Ephemeral notifications.
 *
 * Usage (imperative):
 *   import { toast } from './Toast';
 *   toast.success('Export complete');
 *   toast.error('Failed to load');
 *   toast.info('Processing...');
 *
 * Mount <ToastContainer /> once at the app root (AppLayout handles this).
 */

import { ReactNode, useState, useEffect, useCallback } from 'react';
import { theme } from '../../theme';
import { Icon } from './Icons';

type ToastKind = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  title: ReactNode;
  description?: ReactNode;
  duration?: number;
}

let listeners: ((t: Toast) => void)[] = [];
let nextId = 1;

function emit(kind: ToastKind, title: ReactNode, description?: ReactNode, duration = 4000) {
  const t: Toast = { id: nextId++, kind, title, description, duration };
  listeners.forEach(l => l(t));
}

export const toast = {
  success: (title: ReactNode, description?: ReactNode) => emit('success', title, description),
  error:   (title: ReactNode, description?: ReactNode) => emit('error', title, description, 6000),
  warning: (title: ReactNode, description?: ReactNode) => emit('warning', title, description),
  info:    (title: ReactNode, description?: ReactNode) => emit('info', title, description),
};

function kindStyles(kind: ToastKind) {
  switch (kind) {
    case 'success': return { color: theme.semantic.success, icon: <Icon.CheckCircle size={16} /> };
    case 'error':   return { color: theme.semantic.danger,  icon: <Icon.AlertCircle size={16} /> };
    case 'warning': return { color: theme.semantic.warning, icon: <Icon.AlertTriangle size={16} /> };
    case 'info':    return { color: theme.brand.cyan,       icon: <Icon.Info size={16} /> };
  }
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts(ts => ts.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const listener = (t: Toast) => {
      setToasts(prev => [...prev, t]);
      setTimeout(() => remove(t.id), t.duration);
    };
    listeners.push(listener);
    return () => { listeners = listeners.filter(l => l !== listener); };
  }, [remove]);

  return (
    <>
      <style>{`
        @keyframes meridian-toast-in {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        bottom: theme.space[5],
        right: theme.space[5],
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: theme.space[2],
        zIndex: theme.z.toast,
        pointerEvents: 'none',
      }}>
        {toasts.map(t => {
          const { color, icon } = kindStyles(t.kind);
          return (
            <div
              key={t.id}
              role="status"
              onClick={() => remove(t.id)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.space[3],
                background: theme.surface.elevated,
                border: `1px solid ${theme.borderColor.default}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: theme.radius.md,
                padding: `${theme.space[3]} ${theme.space[4]}`,
                minWidth: 280,
                maxWidth: 400,
                boxShadow: theme.shadow.elevated,
                pointerEvents: 'auto',
                cursor: 'pointer',
                animation: 'meridian-toast-in 180ms ease',
                fontFamily: theme.font.ui,
              }}
            >
              <span style={{ color, display: 'flex', alignItems: 'center', flexShrink: 0, marginTop: 1 }}>
                {icon}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: theme.fontSize.base,
                  fontWeight: theme.fontWeight.medium,
                  color: theme.text.primary,
                }}>
                  {t.title}
                </div>
                {t.description && (
                  <div style={{
                    fontSize: theme.fontSize.sm,
                    color: theme.text.tertiary,
                    marginTop: 2,
                  }}>
                    {t.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════ DIVIDER ═══════════════ */
/* Simple horizontal or vertical divider. */

export function Divider({
  orientation = 'horizontal',
  label,
  style,
}: {
  orientation?: 'horizontal' | 'vertical';
  label?: ReactNode;
  style?: React.CSSProperties;
}) {
  if (orientation === 'vertical') {
    return (
      <div style={{
        width: 1,
        alignSelf: 'stretch',
        background: theme.borderColor.subtle,
        ...style,
      }} />
    );
  }
  if (label) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.space[3],
        ...style,
      }}>
        <div style={{ flex: 1, height: 1, background: theme.borderColor.subtle }} />
        <span style={{
          fontSize: theme.fontSize.xs,
          color: theme.text.tertiary,
          fontWeight: theme.fontWeight.medium,
          letterSpacing: theme.letterSpacing.wide,
          textTransform: 'uppercase',
        }}>
          {label}
        </span>
        <div style={{ flex: 1, height: 1, background: theme.borderColor.subtle }} />
      </div>
    );
  }
  return <div style={{ height: 1, background: theme.borderColor.subtle, ...style }} />;
}

/* ═══════════════ SECTION HEADING ═══════════════ */
/* Small uppercase heading used to label a section within a page. */

export function SectionHeading({ children, action, style }: {
  children: ReactNode;
  action?: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.space[3],
      marginBottom: theme.space[3],
      ...style,
    }}>
      <div style={{
        fontSize: theme.fontSize.xs,
        fontWeight: theme.fontWeight.semibold,
        letterSpacing: theme.letterSpacing.wide,
        textTransform: 'uppercase',
        color: theme.text.tertiary,
      }}>
        {children}
      </div>
      {action}
    </div>
  );
}
